import { ArrowRight, Flame, Scale, Sparkles, Target } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { Entry, UsualMeal } from '../../shared/api';
import { APP_NAME } from '../../shared/brand';
import { addDays, friendlyDay, longDate, MEAL_LABELS, mealForHour, shortDay, type ISODate, type Meal } from '../../shared/dates';
import { BASE_KEYS } from '../../shared/nutrients';
import type { WeightSummary } from '../../shared/trend';
import { formatWeight, type WeightUnit } from '../../shared/units';
import { useCopyEntries, useDay, useSettings, useUsual, useWeights } from '../api/hooks';
import { Composer } from '../components/Composer';
import { EntrySheet } from '../components/EntrySheet';
import { useLogItems } from '../components/log/useLogItems';
import { MealSection } from '../components/MealSection';
import { MacroBars, NutrientChips } from '../components/NutrientProgress';
import { Ring } from '../components/Ring';
import { WeekStrip } from '../components/WeekStrip';
import { kcal } from '../lib/format';
import { localHour } from '../lib/today';
import { useUI } from '../lib/ui';
import { useOpenMeals } from '../lib/useOpenMeals';
import { weightHeadlineCopy } from '../lib/weightHeadline';

function weightSubtitle(summary: WeightSummary | undefined, unit: WeightUnit, today: ISODate): string {
  if (!summary) return 'Weigh in a few times a week to see your trend.';
  const bits: string[] = [];
  if (summary.changes.d7 != null) bits.push(`${formatWeight(summary.changes.d7, unit, { signed: true })} / 7 days`);
  if (summary.changes.d30 != null) bits.push(`${formatWeight(summary.changes.d30, unit, { signed: true })} / 30 days`);
  if (bits.length > 0) return bits.join(' · ');
  if (summary.rate) return `${formatWeight(summary.rate.kgPerWeek, unit, { signed: true })} per week`;
  // A new stretch has no week of its own yet.
  if (summary.since && summary.startKg != null) return `Since ${shortDay(summary.since, today)} · started at ${formatWeight(summary.startKg, unit)}`;
  return 'Weigh in a few times a week to see your trend.';
}

export function TodayPage() {
  const { today, selectedDate, setSelectedDate, openLog, openWhatFits, openWeighIn, toast } = useUI();
  const navigate = useNavigate();
  const day = useDay(selectedDate, today);
  const settings = useSettings();
  const weights = useWeights('30', today);
  const copy = useCopyEntries();
  const isToday = selectedDate === today;
  const nextMeal = mealForHour(localHour());
  const usual = useUsual(selectedDate, nextMeal);
  const { log } = useLogItems(selectedDate, nextMeal);
  const [editing, setEditing] = useState<Entry | null>(null);
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const tracked = useMemo(() => settings.data?.tracking.filter((t) => t.tracked).map((t) => t.key) ?? [...BASE_KEYS], [settings.data]);
  // Meals showing their full nutrition. Kept on this device for every day; without storage they start closed.
  const [openMeals, toggleMeal] = useOpenMeals();
  const aiEnabled = settings.data?.ai.enabled ?? false;
  const unit = settings.data?.profile.weightUnit ?? 'lb';

  const data = day.data;
  // Null when counting without goals: Today shows what was eaten, with no budget or "left".
  const goal = data?.targets.find((t) => t.key === 'kcal')?.amount ?? null;
  const eaten = data?.totals.kcal ?? 0;
  const left = goal === null ? 0 : goal - eaten;
  const proteinTarget = data?.targets.find((t) => t.key === 'protein')?.amount ?? 0;
  const proteinLeft = Math.max(0, proteinTarget - (data?.totals.protein ?? 0));
  const summary = weights.data?.summary;
  const windowDays = weights.data?.trendWindowDays ?? settings.data?.profile.trendWindowDays ?? 14;
  const headline = weightHeadlineCopy(summary, windowDays, today);

  const copying = useRef(false);
  const copyYesterday = (meal: Meal) => {
    if (copying.current) return; // a double tap must not copy twice
    copying.current = true;
    copy.mutate(
      { fromDate: addDays(selectedDate, -1), toDate: selectedDate, meal },
      {
        onSuccess: (created) => toast(`Copied ${created.length} ${created.length === 1 ? 'item' : 'items'} from yesterday`),
        onError: (e) => toast(e.message, { tone: 'error' }),
        onSettled: () => {
          copying.current = false;
        },
      },
    );
  };

  const logUsual = (u: UsualMeal) =>
    void log(
      u.items.map((i) => ({ name: i.name, brand: i.brand, foodRef: i.foodRef, grams: i.grams, per100: i.per100, servings: [], source: 'usual' as const })),
    );

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">{friendlyDay(selectedDate, today)}</h1>
          <p className="page__sub">{longDate(selectedDate)}</p>
        </div>
        <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
          {settings.data?.ai.provider === 'stub' && (
            <span className="chip chip--ai" title="AI answers are simulated offline from your data — no keys or network needed">
              <Sparkles size={14} /> Demo AI
            </span>
          )}
          {data && data.streak > 0 && (
            <span className="chip chip--accent" title="Days in a row with something you logged">
              <Flame size={15} /> {data.streak}-day streak
            </span>
          )}
        </div>
      </div>

      <WeekStrip selected={selectedDate} today={today} onSelect={setSelectedDate} />

      <section className="card card--pad" aria-label="Daily budget">
        {day.isLoading || !data ? (
          <div className="budget">
            <div className="skeleton" style={{ width: 148, height: 148, borderRadius: '50%' }} />
            <div className="stack">
              <div className="skeleton" style={{ height: 22 }} />
              <div className="skeleton" style={{ height: 22 }} />
              <div className="skeleton" style={{ height: 22 }} />
            </div>
          </div>
        ) : (
          <>
            <div className="budget">
              <Ring eaten={eaten} goal={goal} />
              <div className="stack" style={{ gap: 14 }}>
                <div className="row between small">
                  <span className="muted">
                    Eaten <b className="num" style={{ color: 'var(--ink)' }}>{kcal(eaten)}</b>
                  </span>
                  {goal !== null && (
                    <span className="muted">
                      Goal <b className="num" style={{ color: 'var(--ink)' }}>{kcal(goal)}</b>
                    </span>
                  )}
                </div>
                <MacroBars day={data} defs={defs} />
              </div>
            </div>
            <NutrientChips day={data} defs={defs} />
          </>
        )}
      </section>

      <Composer />

      {isToday && data && goal === null && (
        <button
          className="card card--pad row"
          style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }}
          onClick={() => navigate('/setup', { state: { start: 'goal', returnTo: '/' } })}
        >
          <span className="meal__icon setup-cta__icon" aria-hidden="true">
            <Target size={18} />
          </span>
          <span className="grow">
            <span style={{ display: 'block', fontWeight: 650 }}>Set a goal to see what's left</span>
            <span className="small muted">{APP_NAME} is counting what you eat. With a goal, Today shows what's left for the day.</span>
          </span>
          <ArrowRight size={18} className="muted" />
        </button>
      )}

      {isToday && aiEnabled && data && goal !== null && left > 150 && (
        <button className="card card--pad card--ai row" style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }} onClick={() => openWhatFits({ meal: nextMeal })}>
          <span className="coach__avatar" aria-hidden="true">
            <Sparkles size={18} />
          </span>
          <span className="grow">
            <span style={{ display: 'block', fontWeight: 650 }}>What fits for {MEAL_LABELS[nextMeal].toLowerCase()}?</span>
            <span className="small muted num">
              {kcal(left)} kcal{proteinLeft > 5 ? ` and ${Math.round(proteinLeft)} g protein` : ''} left. Get ideas from foods you like.
            </span>
          </span>
          <ArrowRight size={18} className="muted" />
        </button>
      )}

      <section className="card card--pad" aria-label="Meals" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {data?.meals.map((m) => (
          <MealSection
            key={m.meal}
            view={m}
            yesterdayCount={data.yesterday[m.meal]}
            usual={isToday && m.meal === nextMeal ? usual.data?.[0] : undefined}
            open={openMeals.has(m.meal)}
            defs={defs}
            tracked={tracked}
            targets={data.targets}
            onToggle={() => toggleMeal(m.meal)}
            onAdd={() => openLog({ meal: m.meal, date: selectedDate })}
            onOpenEntry={setEditing}
            onLogUsual={logUsual}
            onCopyYesterday={() => copyYesterday(m.meal)}
          />
        ))}
        {!data && [0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 48, margin: '10px 0' }} />)}
      </section>

      <section className="card card--pad row" aria-label="Weight" style={{ alignItems: 'flex-start' }}>
        <span className="meal__icon" style={{ background: 'var(--surface-2)' }} aria-hidden="true">
          <Scale size={18} />
        </span>
        <div className="grow stack" style={{ gap: 6 }}>
          <div className="row">
            <div className="grow">
              {headline.shown ? (
                <>
                  <div className="tiny muted">{headline.title}</div>
                  <div className="num weight-headline">{formatWeight(headline.shown.kg, unit)}</div>
                </>
              ) : (
                <div style={{ fontWeight: 650 }}>Weight</div>
              )}
            </div>
            <Link to="/progress" className="btn btn--quiet btn--sm">
              Details
            </Link>
            <button className="btn btn--accent btn--sm" onClick={() => openWeighIn()}>
              Weigh in
            </button>
          </div>
          {headline.shown ? (
            <div className="stack" style={{ gap: 2 }}>
              {headline.detail && <div className="small muted num">{headline.detail}</div>}
              {headline.shown.source !== 'trend' && summary?.trendKg != null && (
                <div className="weight-trend num">Trend {formatWeight(summary.trendKg, unit)}</div>
              )}
              <div className="tiny muted num">
                {weightSubtitle(summary, unit, today)}
                {summary?.latestDate === today ? ' · weighed in today' : ''}
              </div>
            </div>
          ) : (
            <div className="small muted">Weigh in a few times a week to see your trend.</div>
          )}
        </div>
      </section>

      <Composer floating />
      {editing && <EntrySheet entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
